import { Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { v4 as uuid } from 'uuid';
import { cloneDeep, isEqual } from 'lodash';

import { ComponentHelperService } from 'Utilities/component-helper.service';
import { UirouterMigrationService } from 'Utilities/uirouter.migration.service';
import { DomainService } from 'OpsCenter/common/services/domain.service';
import { DestinationService } from 'OpsCenter/common/services/destination.service';
import { MerchantCategoriesService } from 'OpsCenter/merchants/merchant-categories.service';
import { SeoText } from './seo-text.model';
import { SeoService } from './seo.service';

@Component({
  selector: 'app-seo-edit',
  template: require('./seo.edit.component.html'),
})

/** @desc List component to display seo's **/
export class SeoEditComponent implements OnInit {
  confirmation = null;
  isLoading = false;
  hasChanges = false;
  texts: Array<any>;
  originalTexts: Array<any>;
  locale: Array<any>;
  actualLocale: string;
  destinationName: string;
  categoryName: string;
  destinationId: number;
  destinationType: object;
  countryId: string;
  categoryId: number;
  language: string;
  newText: SeoText;

  constructor(
    private seoService: SeoService,
    private notificationService: ToastrService,
    private domainService: DomainService,
    private destinationService: DestinationService,
    private merchantCategoriesService: MerchantCategoriesService,
    private componentHelperService: ComponentHelperService,
    private uirouterMigrationService: UirouterMigrationService,
  ) {
    // @TODO This helper function needs to be replaced once the ui-router will be replaced
    let params = this.uirouterMigrationService.getParams('app.seo_edit');
    this.categoryId = params.categoryId || '';
    this.countryId = params.countryId;
    this.destinationId = params.destinationId;
    this.language = params.language;
  }

  ngOnInit() {
    this.retrieveTexts();
    this.getCountryAndCategoryName();
  }

  onTextChanged(ev, text) {
    text.text = ev.target.value;
  }

  checkForChanges() {
    this.hasChanges = !isEqual(this.texts, this.originalTexts);
  }

  setTexts(texts) {
    this.texts = texts;
    this.originalTexts = JSON.parse(JSON.stringify(this.texts));
  }

  /**
   * @desc Function to retrieve SEO texts
   * @return {void}
   */
  retrieveTexts(): void {
    this.isLoading = true;
    this.seoService.retrieveSeo(this.destinationId, this.categoryId, this.language).subscribe(
      res => {
        if (res) {
          this.setTexts(res);
        }
      },
      () => this.notificationService.error("Can't get texts."),
    );

    this.isLoading = false;
    this.getLocales();
  }

  /**
   * @desc Function to get country and category names for menu display
   * @return {void}
   */
  getCountryAndCategoryName(): void {
    this.destinationService.getDestinationById(this.destinationId).subscribe(res => {
      this.destinationName = res.name;
      this.destinationType = res.destinationType;
      this.merchantCategoriesService.getAllCategories().subscribe(res => {
        if (this.categoryId) {
          this.categoryName = this.merchantCategoriesService.getCategoryById(parseInt(this.categoryId), res.categories).name;
        }
      });
    });
  }

  /**
   * @desc Function to get local of SEO specific country
   * @return {void}
   */
  getLocales(): void {
    this.domainService.getLocales(this.countryId).subscribe(res => {
      if (res) {
        this.locale = res;
        this.actualLocale = this.locale[0].slice(0, 2);
      }
    });
  }

  /**
   * @desc Function to add new SEO text to the workset
   * @return {void}
   */
  addText(): void {
    this.newText = new SeoText({
      id: uuid.v4(),
      text: '',
      categoryId: this.categoryId,
      destinationId: this.destinationId,
      locale: this.language,
      temp: true,
    });

    this.texts.push(this.newText);
    this.sortText();
    this.newText = null;
  }

  /**
   * @desc Function to delete an SEO text from the workset and database
   * @param {SeoText} text - The SEO text obejct to be deleted
   * @param {boolean} forEdit - Defines if the text is only present on the workset
   * @return {void}
   */
  deleteText(text: SeoText, forEdit: boolean): void {
    if (forEdit) {
      this.componentHelperService.deleteElementById(this.texts, text.id);
      return;
    }
    if (window.confirm('Are you sure you want to delete this text?')) {
      this.componentHelperService.deleteElementById(this.texts, text.id);
      if (!text.temp) {
        this.isLoading = true;
        this.seoService.deleteText(text.id).subscribe();
      }
      this.notificationService.success('Text deleted !');
      this.sortText();
      this.isLoading = false;
    }
  }

  /**
   * @desc Function to save SEO texts
   * @return {void}
   */
  saveTexts(): void {
    if (window.confirm('By clicking on Save all changes will be live. Are you sure you want to continue?')) {
      this.prepareTextsForSave();
      this.sortText();
      this.seoService.updateSeo(this.texts).subscribe(res => {
        this.setTexts(res);
        this.hasChanges = false;
        this.isLoading = false;
        this.notificationService.success('The Seo content was successfully updated.');
      });
    } else {
      return;
    }
  }

  /**
   * @desc Function to adapt existing texts in order to save them
   * @return {void}
   */
  prepareTextsForSave(): void {
    this.texts.forEach(text => {
      delete text.editMode;
      if (text.temp) {
        text.id = null;
        delete text.temp;
      }
    });
  }

  /**
   * @desc Function to apply sort order on existing texts
   * @return {void}
   */
  sortText(): void {
    const numberOfTexts = this.texts.length - 1;
    this.texts.forEach(text => {
      text.sortOrder = numberOfTexts;
      numberOfTexts--;
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/seo/seo.edit.component.ts